import {
  type ExpandableCatalog,
  type CatalogOption,
  type CatalogImportResult,
} from '../models/catalog';
import { db, type KvRow } from '../db';
import { AbstractBaseRepo } from './baseRepo';

const CATALOG_PREFIX = 'catalog:';
const CATALOG_VERSION_KEY = 'catalog_version';
const CATALOG_USER_MODIFIED_KEY = 'catalog_user_modified_';

function normalizeCatalogOption(option: CatalogOption): CatalogOption {
  return {
    ...option,
    isActive: option.isActive ?? true,
  };
}

function normalizeCatalog(catalog: ExpandableCatalog): ExpandableCatalog {
  return {
    ...catalog,
    behavior: catalog.behavior ?? 'suggestions',
    isUserEditable: catalog.isUserEditable ?? true,
    options: catalog.options.map(normalizeCatalogOption),
  };
}

/**
 * Repository for managing expandable catalogs.
 * Provides CRUD operations with version control and user-change protection.
 */
class CatalogRepoImpl extends AbstractBaseRepo<KvRow, KvRow, KvRow, KvRow, string> {
  protected readonly table = db.kv;
  // KV store is device-local, no sync metadata
  protected readonly useSyncMetadata = false;

  protected getRowId(row: KvRow): string {
    return row.key;
  }

  protected getInputId(input: KvRow): string | null {
    return input.key ?? null;
  }

  protected toListModel(row: KvRow): KvRow {
    return row;
  }

  protected toGetModel(row: KvRow): KvRow {
    return row;
  }

  protected toRow(input: KvRow): KvRow {
    return input;
  }

  /**
   * Get all catalogs from storage
   */
  async getAllCatalogs(): Promise<Record<string, ExpandableCatalog>> {
    const kvRows = await this.table.where('key').startsWith(CATALOG_PREFIX).toArray();

    const catalogs: Record<string, ExpandableCatalog> = {};

    for (const row of kvRows) {
      const catalogId = row.key.replace(CATALOG_PREFIX, '');
      const catalog = normalizeCatalog(row.value as ExpandableCatalog);

      // Ensure catalog has required structure
      if (catalog && catalog.id && catalog.options) {
        catalogs[catalogId] = {
          ...catalog,
          isUserModified: await this.isUserModified(catalogId),
        };
      }
    }

    return catalogs;
  }

  /**
   * Get a specific catalog by ID
   */
  async getCatalog(catalogId: string): Promise<ExpandableCatalog | null> {
    const row = await this.table.get(`${CATALOG_PREFIX}${catalogId}`);

    if (!row?.value) return null;

    const catalog = normalizeCatalog(row.value as ExpandableCatalog);
    return {
      ...catalog,
      isUserModified: await this.isUserModified(catalogId),
    };
  }

  /**
   * Save a catalog to storage
   */
  async saveCatalog(catalog: ExpandableCatalog): Promise<void> {
    const catalogToSave = {
      ...normalizeCatalog(catalog),
      updatedAt: new Date().toISOString(),
    };

    await this.table.put({
      key: `${CATALOG_PREFIX}${catalog.id}`,
      value: catalogToSave,
    });

    // Mark as user modified if it contains user-defined options
    if (catalog.options.some((option) => option.isUserDefined)) {
      await this.markUserModified(catalog.id);
    }
  }

  /**
   * Add a new option to a catalog
   */
  async addCatalogOption(
    catalogId: string,
    option: Pick<CatalogOption, 'label' | 'description'> & { id?: string }
  ): Promise<CatalogOption> {
    const catalog = await this.getCatalog(catalogId);
    if (!catalog) {
      throw new Error(`Catalog "${catalogId}" not found`);
    }
    if (!catalog.isUserEditable) {
      throw new Error(`Catalog "${catalogId}" non modificabile`);
    }

    const newOption: CatalogOption = {
      ...option,
      id: option.id ?? crypto.randomUUID(),
      isUserDefined: true,
      isActive: true,
      createdAt: new Date().toISOString(),
    };

    catalog.options.push(newOption);
    catalog.updatedAt = new Date().toISOString();
    catalog.isUserModified = true;

    await this.saveCatalog(catalog);
    await this.markUserModified(catalogId);

    return newOption;
  }

  /**
   * Update an existing option in a catalog
   */
  async updateCatalogOption(
    catalogId: string,
    optionId: string,
    updates: Partial<Pick<CatalogOption, 'label' | 'description'>>
  ): Promise<void> {
    const catalog = await this.getCatalog(catalogId);
    if (!catalog) {
      throw new Error(`Catalog "${catalogId}" not found`);
    }
    if (!catalog.isUserEditable) {
      throw new Error(`Catalog "${catalogId}" non modificabile`);
    }

    const optionIndex = catalog.options.findIndex((opt) => opt.id === optionId);
    if (optionIndex === -1) {
      throw new Error(`Option "${optionId}" not found in catalog "${catalogId}"`);
    }

    catalog.options[optionIndex] = {
      ...catalog.options[optionIndex],
      ...updates,
    };

    catalog.updatedAt = new Date().toISOString();
    await this.saveCatalog(catalog);
  }

  /**
   * Disable an option from a catalog
   */
  async removeCatalogOption(catalogId: string, optionId: string): Promise<void> {
    const catalog = await this.getCatalog(catalogId);
    if (!catalog) {
      throw new Error(`Catalog "${catalogId}" not found`);
    }
    if (!catalog.isUserEditable) {
      throw new Error(`Catalog "${catalogId}" non modificabile`);
    }

    const optionIndex = catalog.options.findIndex((opt) => opt.id === optionId);
    if (optionIndex === -1) {
      throw new Error(`Option "${optionId}" not found in catalog "${catalogId}"`);
    }

    catalog.options[optionIndex] = {
      ...catalog.options[optionIndex],
      isActive: false,
    };
    catalog.updatedAt = new Date().toISOString();

    await this.saveCatalog(catalog);
  }

  /**
   * Get catalog version for tracking updates
   */
  async getCatalogVersion(): Promise<string | null> {
    const row = await this.table.get(CATALOG_VERSION_KEY);
    return (row?.value as string) || null;
  }

  /**
   * Set catalog version (used when loading defaults)
   */
  async setCatalogVersion(version: string): Promise<void> {
    await this.table.put({
      key: CATALOG_VERSION_KEY,
      value: version,
    });
  }

  /**
   * Check if a catalog has been modified by the user
   */
  async isUserModified(catalogId: string): Promise<boolean> {
    const row = await this.table.get(`${CATALOG_USER_MODIFIED_KEY}${catalogId}`);
    return Boolean(row?.value);
  }

  /**
   * Mark a catalog as user-modified
   */
  async markUserModified(catalogId: string): Promise<void> {
    await this.table.put({
      key: `${CATALOG_USER_MODIFIED_KEY}${catalogId}`,
      value: true,
    });
  }

  /**
   * Reset all catalogs to defaults (removes user data only)
   */
  async resetToDefaults(): Promise<void> {
    // Remove all catalogs
    const catalogKeys = (await this.table.where('key').startsWith(CATALOG_PREFIX).toArray()).map(
      (row) => row.key
    );

    await this.table.bulkDelete(catalogKeys);

    // Remove user modification flags
    const userModifiedKeys = (
      await this.table.where('key').startsWith(CATALOG_USER_MODIFIED_KEY).toArray()
    ).map((row) => row.key);

    await this.table.bulkDelete(userModifiedKeys);

    // Remove version tracking
    await this.table.delete(CATALOG_VERSION_KEY);
  }

  /**
   * Export all catalogs as JSON
   */
  async exportCatalogs(): Promise<string> {
    const catalogs = await this.getAllCatalogs();

    const exportData = {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      catalogs,
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Import catalogs from JSON
   */
  async importCatalogs(jsonString: string): Promise<CatalogImportResult> {
    try {
      const importData = JSON.parse(jsonString);

      // Validate structure
      if (!importData.catalogs || typeof importData.catalogs !== 'object') {
        return {
          success: false,
          catalogsImported: [],
          errors: ['Invalid file format: missing catalogs object'],
        };
      }

      const catalogsImported: string[] = [];
      const errors: string[] = [];
      const warnings: string[] = [];

      // Import each catalog
      for (const [catalogId, catalog] of Object.entries(importData.catalogs)) {
        try {
          // Validate catalog structure
          const catalogToImport = catalog as ExpandableCatalog;
          if (
            !catalogToImport.id ||
            !catalogToImport.label ||
            !Array.isArray(catalogToImport.options)
          ) {
            errors.push(`Invalid catalog structure for "${catalogId}"`);
            continue;
          }

          // Validate options
          for (const option of catalogToImport.options) {
            if (!option.id || !option.label) {
              errors.push(`Invalid option in catalog "${catalogId}"`);
              continue;
            }
          }

          if (errors.some((e) => e.includes(catalogId))) {
            continue;
          }

          // Import the catalog
          await this.saveCatalog({
            ...catalogToImport,
            updatedAt: new Date().toISOString(),
            isUserModified: true, // Mark all imported catalogs as user-modified
          });

          await this.markUserModified(catalogId);
          catalogsImported.push(catalogId);
        } catch (error) {
          errors.push(
            `Failed to import catalog "${catalogId}": ${error instanceof Error ? error.message : 'Unknown error'}`
          );
        }
      }

      return {
        success: errors.length === 0,
        catalogsImported,
        errors: errors.length > 0 ? errors : undefined,
        warnings: warnings.length > 0 ? warnings : undefined,
      };
    } catch (error) {
      return {
        success: false,
        catalogsImported: [],
        errors: [
          `Failed to parse JSON: ${error instanceof Error ? error.message : 'Unknown error'}`,
        ],
      };
    }
  }
}

export const catalogRepo = new CatalogRepoImpl();
